import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import openAILogo from '../../assets/logos/openai-logo.svg';
import { 
  faTimes, 
  faEye, 
  faEyeSlash, 
  faRobot,
  faCheckCircle,
  faExclamationCircle,
  faSpinner
} from '@fortawesome/free-solid-svg-icons';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';

const OpenAIConnectorForm = ({ isOpen, onClose, onSave, editingConnector = null }) => {
  const [formData, setFormData] = useState({
    name: '',
    apiKey: '',
    organizationId: '',
    model: 'gpt-3.5-turbo',
    maxTokens: 2048
  });

  const [showApiKey, setShowApiKey] = useState(false);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [testingConnection, setTestingConnection] = useState(false);

  const modelOptions = [
    { value: 'gpt-3.5-turbo', label: 'GPT-3.5 Turbo (Recommended)' },
    { value: 'gpt-4', label: 'GPT-4' },
    { value: 'gpt-4-turbo', label: 'GPT-4 Turbo' },
    { value: 'gpt-4o', label: 'GPT-4o' }
  ];

  // Populate form data when editing
  useEffect(() => {
    if (editingConnector && isOpen) {
      const credentials = editingConnector.credentials || {};
      setFormData({
        name: editingConnector.name || '',
        apiKey: credentials.api_key || '',
        organizationId: credentials.organization_id || '',
        model: credentials.model || 'gpt-3.5-turbo',
        maxTokens: credentials.max_tokens || 2048
      });
    } else if (!isOpen) {
      // Reset form when modal closes
      setFormData({
        name: '',
        apiKey: '',
        organizationId: '',
        model: 'gpt-3.5-turbo',
        maxTokens: 2048
      });
      setErrors({});
      setShowApiKey(false);
    }
  }, [editingConnector, isOpen]);

  const validateForm = () => {
    const newErrors = {};

    // Required field validation
    if (!formData.name.trim()) newErrors.name = 'Connection name is required';
    if (!formData.apiKey.trim()) newErrors.apiKey = 'API Key is required';

    // Format validation
    if (formData.name && formData.name.length < 3) {
      newErrors.name = 'Connection name must be at least 3 characters';
    }

    if (formData.apiKey && !formData.apiKey.startsWith('sk-')) {
      newErrors.apiKey = 'OpenAI API Key must start with "sk-"';
    }

    if (formData.maxTokens && (formData.maxTokens < 1 || formData.maxTokens > 8192)) {
      newErrors.maxTokens = 'Max tokens must be between 1 and 8192';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  const handleTestConnection = async () => {
    if (!validateForm()) {
      toast.error('Please fix validation errors before testing');
      return;
    }

    setTestingConnection(true);
    try {
      // Simulate API call for testing connection
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // TODO: Replace with actual OpenAI API test call
      const success = Math.random() > 0.3; // 70% success rate for demo
      
      if (success) {
        toast.success('OpenAI connection test successful!');
      } else {
        toast.error('Connection test failed. Please check your API key.');
      }
    } catch (error) {
      toast.error('Connection test failed');
    } finally {
      setTestingConnection(false);
    }
  };

  const handleSave = async () => {
    if (!validateForm()) {
      toast.error('Please fix all errors before saving');
      return;
    }

    setIsLoading(true);
    try {
      const connectorData = {
        name: formData.name,
        service_type: 'openai',
        credentials: {
          api_key: formData.apiKey,
          organization_id: formData.organizationId || undefined,
          model: formData.model,
          max_tokens: parseInt(formData.maxTokens)
        }
      };

      await onSave(connectorData);
      toast.success(editingConnector ? 'OpenAI connector updated successfully!' : 'OpenAI connector created successfully!');
      handleClose();
    } catch (error) {
      toast.error('Failed to save connector');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      name: '',
      apiKey: '',
      organizationId: '',
      model: 'gpt-3.5-turbo',
      maxTokens: 2048
    });
    setErrors({});
    setShowApiKey(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <AnimatePresence>
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden"
        >
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 rounded-lg overflow-hidden bg-white border border-gray-200 dark:border-gray-600 flex items-center justify-center">
                  <img 
                    src={openAILogo} 
                    alt="OpenAI Logo"
                    className="w-8 h-5 object-contain"
                  />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                    {editingConnector ? 'Edit OpenAI Connector' : 'OpenAI Connector'}
                  </h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {editingConnector ? 'Update API key authentication for OpenAI integration' : 'Configure API key authentication for OpenAI integration'}
                  </p>
                </div>
              </div>
              <button
                onClick={handleClose}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              >
                <FontAwesomeIcon icon={faTimes} className="text-xl" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="p-6 space-y-6 max-h-[60vh] overflow-y-auto">
            {/* Connection Name */}
            <div className="space-y-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Connection Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                className={`form-input w-full ${errors.name ? 'border-red-500' : ''}`}
                placeholder="e.g., OpenAI Production"
              />
              {errors.name && (
                <motion.p
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="text-red-500 text-sm flex items-center"
                >
                  <FontAwesomeIcon icon={faExclamationCircle} className="mr-1 text-xs" />
                  {errors.name}
                </motion.p>
              )}
            </div>

            {/* API Key */}
            <div className="space-y-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                API Key <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  type={showApiKey ? 'text' : 'password'}
                  value={formData.apiKey}
                  onChange={(e) => handleInputChange('apiKey', e.target.value)}
                  className={`form-input w-full pr-10 ${errors.apiKey ? 'border-red-500' : ''}`}
                  placeholder="sk-..."
                />
                <button
                  type="button"
                  onClick={() => setShowApiKey(!showApiKey)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <FontAwesomeIcon 
                    icon={showApiKey ? faEyeSlash : faEye} 
                    className="text-sm"
                  />
                </button>
              </div>
              {errors.apiKey && (
                <motion.p
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="text-red-500 text-sm flex items-center"
                >
                  <FontAwesomeIcon icon={faExclamationCircle} className="mr-1 text-xs" />
                  {errors.apiKey}
                </motion.p>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Organization ID */}
              <div className="space-y-1">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Organization ID <span className="text-gray-400">(Optional)</span>
                </label>
                <input
                  type="text"
                  value={formData.organizationId}
                  onChange={(e) => handleInputChange('organizationId', e.target.value)}
                  className="form-input w-full"
                  placeholder="org-..."
                />
              </div>

              {/* Max Tokens */}
              <div className="space-y-1">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Max Tokens
                </label>
                <input
                  type="number"
                  value={formData.maxTokens}
                  onChange={(e) => handleInputChange('maxTokens', e.target.value)}
                  className={`form-input w-full ${errors.maxTokens ? 'border-red-500' : ''}`}
                  placeholder="2048"
                  min="1"
                  max="8192"
                />
                {errors.maxTokens && (
                  <motion.p
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="text-red-500 text-sm flex items-center"
                  >
                    <FontAwesomeIcon icon={faExclamationCircle} className="mr-1 text-xs" />
                    {errors.maxTokens}
                  </motion.p>
                )}
              </div>
            </div>

            {/* Model Selection */}
            <div className="space-y-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Model
              </label>
              <select
                value={formData.model}
                onChange={(e) => handleInputChange('model', e.target.value)}
                className="form-input w-full"
              >
                {modelOptions.map(model => (
                  <option key={model.value} value={model.value}>
                    {model.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Helper Text */}
            <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg">
              <h4 className="text-sm font-medium text-green-900 dark:text-green-200 mb-2">
                How to get OpenAI API Key:
              </h4>
              <ul className="text-sm text-green-700 dark:text-green-300 space-y-1">
                <li>• Go to <a href="https://platform.openai.com/api-keys" target="_blank" rel="noopener noreferrer" className="underline">platform.openai.com/api-keys</a></li>
                <li>• Click "Create new secret key"</li>
                <li>• Copy the key starting with "sk-"</li>
                <li>• Organization ID is optional for most users</li>
              </ul>
            </div>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 bg-gray-50 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700">
            <div className="flex justify-between">
              <button
                onClick={handleTestConnection}
                disabled={testingConnection}
                className={`btn ${testingConnection ? 'btn-disabled' : 'btn-secondary'}`}
              >
                {testingConnection ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} className="mr-2 animate-spin" />
                    Testing...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faCheckCircle} className="mr-2" />
                    Test Connection
                  </>
                )}
              </button>
              
              <div className="flex space-x-3">
                <button
                  onClick={handleClose}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={isLoading}
                  className={`btn ${isLoading ? 'btn-disabled' : 'btn-primary'}`}
                >
                  {isLoading ? (
                    <>
                      <FontAwesomeIcon icon={faSpinner} className="mr-2 animate-spin" />
                      {editingConnector ? 'Updating...' : 'Creating...'}
                    </>
                  ) : (
                    editingConnector ? 'Update Connector' : 'Create Connector'
                  )}
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

export default OpenAIConnectorForm;